import { useState } from 'react';

import { OutgoingProductCheckoutSessionDto } from 'api/dtos/products/checkout';
import { NormalizedCart } from '../../types';
import usePaymentMethod from 'hooks/usePaymentMethod';
import {
  useConfirmCheckoutSession,
  useStartCheckoutSession,
  useUpdateCheckoutSession
} from 'api/queries/organizations/memberships-checkout';
import useCart from './useCart';
import { Card } from 'legacy/common/types/models';
import { GroupDto } from 'legacy/groups/types';
import { logEvent } from 'legacy/common/utils/amplitude';
import { EVENT_REDIRECT_ON_CHECKOUT_SESSION_COMPLETED_ERROR } from 'legacy/common/constants/events';

const useCheckoutSession = (
  organization: GroupDto,
  cart: ReturnType<typeof useCart>,
  paymentMethod: ReturnType<typeof usePaymentMethod>,
  isCheckoutExpired: boolean
) => {
  const [checkoutSession, setCheckoutSession] =
    useState<OutgoingProductCheckoutSessionDto | null>(null);

  const startCheckoutSession = useStartCheckoutSession();
  const updateCheckoutSession = useUpdateCheckoutSession();
  const confirmCheckoutSession = useConfirmCheckoutSession();

  /**
   * Starts the checkout process.
   */
  const startCheckout = () => {
    if (!organization?.id) {
      return;
    }

    startCheckoutSession.mutate(organization.id, {
      onSuccess: (data) => {
        setCheckoutSession(data);
      }
    });
  };

  /**
   * Update the checkout session with given parameters and options.
   */
  const updateCheckout = ({
    updatedCart,
    updatedStripePaymentMethodType,
    updatedCreditCardId,
    updatedUserCards,
    updatedDiscountCode
  }: {
    updatedCart?: NormalizedCart;
    updatedStripePaymentMethodType?: 'klarna' | 'affirm' | null;
    updatedCreditCardId?: string;
    updatedUserCards?: Card[];
    updatedDiscountCode?: string;
  }) =>
    new Promise((resolve, reject) => {
      if (!checkoutSession?.paymentIntentId || !organization?.id) {
        reject(
          new Error('paymentIntentId not provided or event id not provided')
        );
      }

      const selectedCreditCardId =
        updatedCreditCardId || paymentMethod.selectedCard.value;

      const cartData = Object.fromEntries(
        Object.entries(updatedCart || cart.selectedProducts).map(
          ([key, value]) => [key, value.metadata.quantity]
        )
      );

      startCheckoutSession.reset();
      confirmCheckoutSession.reset();

      updateCheckoutSession.mutate(
        {
          organizationId: organization.id,
          paymentIntentId: checkoutSession?.paymentIntentId,
          data: {
            cart: cartData,
            // Logic here is a bit weird but, basically, the credit card id is never empty unless the user does NOT have a card.
            // If the user selects buy now pay later as payment method, the stripe payment method type will arrive as the selected buy now pay later.
            // If the user selects credit card as payment method but doesn't select a card, the stripe payment method type will arrive as null.
            // But the credit card id will NOT be empty.
            // This means that in order to set the selected payment method as buy now pay later, we need to listen for the passed parameter.
            // And in order to set the selected payment method as credit card, we need to listen for the passed parameter to be null.
            stripePaymentMethodType:
              updatedStripePaymentMethodType || undefined,
            stripePaymentMethodId:
              !updatedStripePaymentMethodType && selectedCreditCardId
                ? paymentMethod.getCardStripePaymentMethodIdFromCardId(
                    selectedCreditCardId,
                    updatedUserCards
                  )
                : undefined,
            discountCode: updatedDiscountCode || undefined
          }
        },
        {
          onSuccess: (data) => {
            setCheckoutSession(data);
            resolve(data);
          },
          onError: (error) => {
            reject(error);
          }
        }
      );
    });

  /**
   * Confirms the checkout session with the provided data.
   * Also logs the event in Google Analytics if transaction was successful.
   */
  const confirmCheckout = () =>
    new Promise((resolve, reject) => {
      if (!checkoutSession?.paymentIntentId || !organization?.id) {
        reject(
          new Error(
            'paymentIntentId not provided or organization id not provided'
          )
        );
      }

      startCheckoutSession.reset();
      updateCheckoutSession.reset();

      confirmCheckoutSession.mutate(
        {
          organizationId: organization.id,
          paymentIntentId: checkoutSession.paymentIntentId
        },
        {
          onSuccess: (data) => {
            if (data?.redirectUrl) {
              // Write the checkout data to local storage to be used in the StripeStatusBanner post checkout
              localStorage.setItem(
                'bounceCurrentMembershipCheckoutData',
                JSON.stringify({
                  organizationId: organization.id,
                  organizationName: organization.name,
                  numberOfProducts: Object.values(checkoutSession.cart).reduce(
                    (acc, curr) => acc + curr,
                    0
                  ),
                  value: checkoutSession.total || 0,
                  currency: checkoutSession.currency || 'USD'
                })
              );

              // Create an empty document to prevent any unsaved changes dialogs
              document.open();
              document.write(
                `<html><head><meta http-equiv="refresh" content="0; url=${data.redirectUrl}"></head><body>Redirecting...</body></html>`
              );
              document.close();
            } else {
              resolve(null);
            }
          },
          onError: (error) => {
            if (error.code === 'checkoutSessionCompleted') {
              logEvent(EVENT_REDIRECT_ON_CHECKOUT_SESSION_COMPLETED_ERROR);
              return resolve(null);
            }

            return reject(error);
          }
        }
      );
    });

  return {
    sessionData: checkoutSession,
    startCheckout,
    updateCheckout,
    confirmCheckout,
    isLoadingCheckout:
      startCheckoutSession.isLoading ||
      updateCheckoutSession.isLoading ||
      confirmCheckoutSession.isLoading,
    isStartingCheckout: startCheckoutSession.isLoading,
    isUpdatingCheckout: updateCheckoutSession.isLoading,
    checkoutError:
      startCheckoutSession.error ||
      updateCheckoutSession.error ||
      confirmCheckoutSession.error,
    isCheckoutDisabled: Boolean(
      startCheckoutSession.error || isCheckoutExpired
    ),
    isConfirmingCheckout: confirmCheckoutSession.isLoading,
    confirmCheckoutError: confirmCheckoutSession.error,
    isCheckoutSuccess: confirmCheckoutSession.isSuccess
  };
};

export default useCheckoutSession;
